import {
  getAgentSearch,
  getDocumentFiles,
  getTree,
  isContentPending,
  isIndexPending,
  listAgentSearches,
  type ClipboardTree,
} from "@/features/clipboard/api/clipboard";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export const clipboardTreeQueryKeyRoot = ["clipboard-tree"] as const;

export const clipboardTreeQueryKey = (path: string) =>
  [...clipboardTreeQueryKeyRoot, path] as const;

export const clipboardStatusQueryKey = ["clipboard-status"] as const;

export const clipboardAgentSearchesQueryKey = ["clipboard-agent-searches"] as const;

export const clipboardAgentSearchQueryKey = (searchId: string) =>
  ["clipboard-agent-search", searchId] as const;

export const clipboardDocumentFilesQueryKey = (docId: string) =>
  ["clipboard-document-files", docId] as const;

export const useInvalidateClipboard = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: clipboardTreeQueryKeyRoot });
    queryClient.invalidateQueries({ queryKey: clipboardStatusQueryKey });
  }, [queryClient]);
};

export const invalidateClipboardAgentSearches = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: clipboardAgentSearchesQueryKey });

export const useClipboardTreeQuery = (
  path: string,
  ownerId: string | null | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [...clipboardTreeQueryKey(path), ownerId],
    queryFn: () => getTree(path, ownerId ?? undefined),
    enabled,
    // While any doc is still parsing/indexing, quietly poll so its row flips to
    // "Searchable" on its own — no manual refresh, no disruptive spinner.
    refetchInterval: (query) => {
      const data = query.state.data;
      const pending = data?.documents?.some(
        (d) =>
          isContentPending(d.content_status ?? null) ||
          isIndexPending(d.index_status ?? null),
      );
      return pending ? 4000 : false;
    },
  });

export const useClipboardAgentSearchesQuery = (enabled: boolean) =>
  useQuery({
    queryKey: clipboardAgentSearchesQueryKey,
    queryFn: listAgentSearches,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.some((search) =>
        ["pending", "dispatched", "running", "executing", "retry"].includes(
          search.status,
        ),
      )
        ? 1000
        : false,
  });

export const useClipboardAgentSearchQuery = (
  searchId: string | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: clipboardAgentSearchQueryKey(searchId ?? ""),
    queryFn: () => getAgentSearch(searchId!),
    enabled: enabled && !!searchId,
    // Poll tightly while in flight so the planned command (and then results)
    // surface within a fraction of a second of each phase transition.
    refetchInterval: (query) =>
      query.state.data &&
      ["pending", "dispatched", "running", "executing", "retry"].includes(
        query.state.data.status,
      )
        ? 400
        : false,
  });

export const useClipboardDocumentFilesQuery = (
  docId: string | undefined,
  ownerId: string | null | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [...clipboardDocumentFilesQueryKey(docId ?? ""), ownerId],
    queryFn: () => getDocumentFiles(docId!, ownerId ?? undefined),
    enabled: enabled && !!docId,
    refetchInterval: (query) =>
      isContentPending(query.state.data?.document.content_status ?? null)
        ? 4000
        : false,
  });

/** Optimistic cache edit; returns a rollback that restores pre-delete snapshots. */
export const removeDocsFromClipboardCache = (
  queryClient: QueryClient,
  ids: string[],
) => {
  const idSet = new Set(ids);
  const snapshots = queryClient.getQueriesData<ClipboardTree>({
    queryKey: clipboardTreeQueryKeyRoot,
  });
  queryClient.setQueriesData<ClipboardTree>(
    { queryKey: clipboardTreeQueryKeyRoot },
    (old) =>
      old ? { ...old, documents: old.documents.filter((d) => !idSet.has(d.id)) } : old,
  );
  return () => {
    for (const [key, data] of snapshots) queryClient.setQueryData(key, data);
  };
};

/** Optimistic cache edit; returns a rollback that restores pre-delete snapshots. */
export const removeFolderFromClipboardCache = (
  queryClient: QueryClient,
  name: string,
) => {
  const snapshots = queryClient.getQueriesData<ClipboardTree>({
    queryKey: clipboardTreeQueryKeyRoot,
  });
  queryClient.setQueriesData<ClipboardTree>(
    { queryKey: clipboardTreeQueryKeyRoot },
    (old) =>
      old ? { ...old, folders: old.folders.filter((f) => f.name !== name) } : old,
  );
  return () => {
    for (const [key, data] of snapshots) queryClient.setQueryData(key, data);
  };
};
